/* ──────────────────────────────────────────────────────────────
 *  ComputeConfirmDialog — Disclaimer before running compute
 *
 *  Shown when the user clicks the compute button. Requires explicit
 *  confirmation before proceeding with the sampling/compute workflow.
 *
 *  Ported from BushingApp.compute_confirm_dialog (optibush.py).
 * ────────────────────────────────────────────────────────────── */

import React from 'react';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

import DialogShell from '@/components/DialogShell';
import { useUIStore } from '@/stores/useUIStore';

interface ComputeConfirmDialogProps {
  onProceed: () => void;
}

const ComputeConfirmDialog: React.FC<ComputeConfirmDialogProps> = ({ onProceed }) => {
  const open = useUIStore((s) => s.computeConfirmDialogOpen);
  const closeDialog = useUIStore((s) => s.closeDialog);

  const handleClose = () => closeDialog('computeConfirmDialog');

  const handleProceed = () => {
    handleClose();
    onProceed();
  };

  return (
    <DialogShell
      open={open}
      onClose={handleClose}
      title="Disclaimer"
      titleColor="#5e503f"
      icon={<WarningAmberIcon sx={{ fontSize: 35, color: 'warning.main' }} />}
      maxWidth={650}
      actions={
        <>
          <Button
            variant="contained"
            color="info"
            onClick={handleClose}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="info"
            onClick={handleProceed}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            Proceed
          </Button>
        </>
      }
    >
      <Typography sx={{ fontSize: '16.5px', lineHeight: 1.8, color: '#333', mb: 1.5 }}>
        Optibush should be used to generate initial design concepts only. Optibush results
        may vary from FEA by as much as ±15%.
        <strong> In some special cases the variation might even exceed 30%.</strong>
      </Typography>
      <Typography sx={{ fontSize: '16.5px', lineHeight: 1.8, color: '#d32f2f' }}>
        Optibush results should not be used directly for tech-review presentations OR
        quotation <u>without</u> proper FEA validation.
      </Typography>
    </DialogShell>
  );
};

export default ComputeConfirmDialog;
